import React, { useState, useEffect, useMemo } from 'react';
import { Box, Paper, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Button, Checkbox, FormControlLabel, IconButton, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { ColDef } from 'ag-grid-community';
import dayjs from 'dayjs';
import * as ExcelJS from 'exceljs';
import { CommodityManagementApi } from '../../../../../../Apis/Apis';
import { PositionModel, PositionType, Side, OptionType, GetCustomerDataResponse, GetCustomerSettingsResponse, LocationModel } from '../../../../../../Generated/Commodity-Management-Api';
import { StoneXDatePicker } from '../../../../../StoneXMui';
import { StoneXGrid, StoneXLoading, StoneXRow } from '../../../../../StoneX';
import { useLoadingState } from '../../../../../../Hooks';
import { 
    MtmClosedReportRow, 
    parseCustomData, 
    getToolDescription, 
    calculatePurchaseCost,
    calculateRealizedValue,
    calculateNetValueGainLoss,
    ClosedReportSummary
} from './Types';

interface MtmClosedReportProps {
    customerData?: GetCustomerDataResponse;
    customerSettings?: GetCustomerSettingsResponse;
    defaultQuoteDate?: Date;
    defaultBudgetStartDate?: Date;
    defaultBudgetEndDate?: Date;
}

interface FiscalYearData {
    fiscalYearLabel: string;
    startDate: string;
    endDate: string;
    rows: MtmClosedReportRow[];
    summary: ClosedReportSummary;
}

/**
 * Which parts of the report to produce. 'detail' and 'summary' are the two
 * original modes (the old "Summary Only" checkbox); 'both' runs them together,
 * showing the detail grids alongside a consolidated cross-year summary and
 * exporting each to its own Excel tab.
 */
type ReportView = 'detail' | 'summary' | 'both';

const REPORT_VIEWS: { value: ReportView; label: string }[] = [
    { value: 'detail', label: 'Detail' },
    { value: 'summary', label: 'Summary Only' },
    { value: 'both', label: 'Both' },
];

/** One consolidated row per fiscal year, plus a grand total. */
interface ConsolidatedSummaryRow {
    fiscalYearLabel: string;
    totalQuantity: number;
    totalPurchaseCost: number;
    totalRealizedValue: number;
    totalNetValueGainLoss: number;
    totalFees: number;
}

const MONTHS = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
];

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MMBTU_PER_CONTRACT = 10000;

// Fiscal month offsets are zero based, as in the site settings: 6 means the fiscal year runs
// July to June and 4 means May to April.
const DEFAULT_FISCAL_MONTH_OFFSET = 6;

// Piedmont's jurisdictions keep their own fiscal years, which one site setting cannot express,
// so they are fixed here by location name. Any other location follows the site setting.
const LOCATION_FISCAL_MONTH_OFFSETS: Record<string, number> = {
    'north carolina': 6,
    'south carolina': 4,
};

const getLocationFiscalMonthOffset = (locationName: string): number | undefined =>
    LOCATION_FISCAL_MONTH_OFFSETS[locationName.trim().toLowerCase()];

// The year a fiscal year ends in, for a budget month (1-12) and year. With offset 6, July 2026
// through June 2027 all belong to the fiscal year ending in 2027.
const getFiscalYear = (month: number, year: number, fiscalMonthOffset: number): number => {
    if (month <= fiscalMonthOffset) {
        return year;
    }
    return year + 1;
};

const MtmClosedReport: React.FC<MtmClosedReportProps> = ({
    customerData,
    customerSettings,
    defaultQuoteDate,
}) => {
    const loadingState = useLoadingState();
    const [positions, setPositions] = useState<PositionModel[]>([]);
    const [quoteDate, setQuoteDate] = useState<Date>(defaultQuoteDate || new Date());
    const [includeFees, setIncludeFees] = useState<boolean>(false);
    const [reportView, setReportView] = useState<ReportView>('detail');
    const [hasRun, setHasRun] = useState<boolean>(false);
    const [expandedYears, setExpandedYears] = useState<Set<string>>(new Set());

    const locations: LocationModel[] = customerData?.locations || [];

    // Default to first location
    const [selectedLocation, setSelectedLocation] = useState<number | undefined>(undefined);

    const selectedLocationName = locations.find(l => l.locationId === selectedLocation)?.locationName || '';

    useEffect(() => {
        if (locations.length > 0 && selectedLocation === undefined) {
            setSelectedLocation(locations[0].locationId);
        }
    }, [locations, selectedLocation]);

    // A location with its own fiscal year wins over the site setting.
    const fiscalMonthOffset = getLocationFiscalMonthOffset(selectedLocationName) ?? customerSettings?.fiscalMonthOffset ?? DEFAULT_FISCAL_MONTH_OFFSET;

    // Budget date range - default to fiscal year start, 5 years prior, through current month
    const [startMonth, setStartMonth] = useState<number>(fiscalMonthOffset + 1);
    const [startYear, setStartYear] = useState<number>(dayjs().year() - 2);
    // dayjs().month() is 0-indexed while the MONTHS options are 1-12, so the
    // bare call defaulted this to last month. +1 makes it the current month.
    const [endMonth, setEndMonth] = useState<number>(dayjs().month() + 1);
    const [endYear, setEndYear] = useState<number>(dayjs().year());

    // Follow the fiscal year start when the settings arrive or the location changes
    useEffect(() => {
        setStartMonth(fiscalMonthOffset + 1);
    }, [fiscalMonthOffset]);

    const fetchPositions = async () => {
        const response = await CommodityManagementApi.listPositions(
            loadingState.setLoadingState,
            dayjs(quoteDate).format('YYYY-MM-DD'),
            true,  // includeExpirationDates - needed to populate position.expirationDate
            true,  // includeMarketPrices
            true,  // useDisplayUnits
            undefined, // scenarioMarketPrices
            undefined, // commodityFilter - removed since only one commodity
            selectedLocation ? [selectedLocation] : undefined,
            undefined, // counterpartyFilter
            undefined, // tradeStartDate
            undefined, // tradeEndDate
            startYear,
            startMonth,
            undefined, // budgetLabel
            endYear,
            endMonth
        );

        setPositions(response.data.rows || []);
        setHasRun(true);
        setExpandedYears(new Set()); // Collapse all on new data
    };

    const handleSubmit = () => {
        fetchPositions();
    };

    // 'both' produces each part, so the detail grids and the consolidated
    // summary are each shown/exported unless the other mode is selected alone.
    // 'both' produces each part, so the detail grids and the consolidated
    // summary are each shown/exported unless the other mode is selected alone.
    const showDetail = reportView !== 'summary';
    const showSummary = reportView !== 'detail';

    const downloadWorkbook = async (workbook: ExcelJS.Workbook) => {
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `HedgingMTMClosedPositionsReport_${dayjs(quoteDate).format('YYYYMMDD')}.xlsx`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    };

    const handleDownloadExcel = async () => {
        if (fiscalYearData.length === 0) return;

        const HEADERS = [
            'Period',
            'Tool',
            'Counterparty',
            'Original Trade Date',
            'MMBtus Purchased Per Month',
            'Strike/Fixed Price',
            'Purchase Price',
            'Purchase Cost/Proceeds',
            'Trade / Expiration Date',
            'Expiration Price',
            'Realized Value',
            'Net Value Realized Gain or (Loss)',
            'Fees',
        ];

        const workbook = new ExcelJS.Workbook();
        const reportAsOf = `Report as of ${dayjs(quoteDate).format('M/D/YYYY')} Close`;
        const lastCol = HEADERS.length; // 1-indexed total columns

        const currencyFmt = '"$"#,##0.00;("$"#,##0.00)';
        const priceFmt = '"$"#,##0.0000;("$"#,##0.0000)';
        const strikeFmt = '"$"#,##0.000;("$"#,##0.000)';
        const qtyFmt = '#,##0';

        // Summary tab first, so the workbook opens on the overview when both
        // parts are exported.
        if (showSummary) {
            const summarySheet = workbook.addWorksheet('Summary');
            const SUMMARY_HEADERS = [
                'Fiscal Year',
                'MMBtus',
                'Purchase Cost/Proceeds',
                'Realized Value',
                'Net Value Realized Gain or (Loss)',
                'Fees',
            ];
            const summaryLastCol = SUMMARY_HEADERS.length;

            summarySheet.columns = [
                { width: 28 }, // Fiscal Year
                { width: 16 }, // MMBtus
                { width: 22 }, // Purchase Cost
                { width: 16 }, // Realized Value
                { width: 30 }, // Net Gain/Loss
                { width: 12 }, // Fees
            ];

            summarySheet.addRow([`${selectedLocationName} MTM Closed Positions Summary`.trim()]);
            summarySheet.mergeCells(1, 1, 1, summaryLastCol);
            const summaryTitle = summarySheet.getCell(1, 1);
            summaryTitle.font = { bold: true, size: 14 };
            summaryTitle.alignment = { horizontal: 'center', vertical: 'middle' };

            summarySheet.addRow([reportAsOf]);
            summarySheet.mergeCells(2, 1, 2, summaryLastCol);
            const summarySubtitle = summarySheet.getCell(2, 1);
            summarySubtitle.font = { bold: true };
            summarySubtitle.alignment = { horizontal: 'center', vertical: 'middle' };

            const summaryHeaderRow = summarySheet.addRow(SUMMARY_HEADERS);
            summaryHeaderRow.font = { bold: true };
            summaryHeaderRow.alignment = { horizontal: 'center', vertical: 'middle' };

            consolidatedSummaryRows.forEach(row => {
                const added = summarySheet.addRow([
                    row.fiscalYearLabel,
                    row.totalQuantity,
                    row.totalPurchaseCost,
                    row.totalRealizedValue,
                    row.totalNetValueGainLoss,
                    row.totalFees,
                ]);
                if (row.fiscalYearLabel === 'TOTAL') {
                    added.font = { bold: true };
                }
                added.getCell(2).numFmt = qtyFmt;
                added.getCell(3).numFmt = currencyFmt;
                added.getCell(4).numFmt = currencyFmt;
                added.getCell(5).numFmt = currencyFmt;
                added.getCell(6).numFmt = currencyFmt;
            });
        }

        if (!showDetail) {
            await downloadWorkbook(workbook);
            return;
        }

        fiscalYearData.forEach(fyData => {
            // Sheet name: fiscal year range, sanitized to <=31 chars, Excel-safe
            const rawName = `${fyData.startDate.replace(/\s+/g, '')}-${fyData.endDate.replace(/\s+/g, '')}`;
            const safeName =
                rawName.replace(/[\\/*?:[\]]/g, '').substring(0, 31)
                || `FY${fyData.fiscalYearLabel.substring(0, 28)}`;

            const sheet = workbook.addWorksheet(safeName);

            // Column widths (set before adding rows so they apply cleanly)
            sheet.columns = [
                { width: 16 }, // Period
                { width: 12 }, // Tool
                { width: 14 }, // Counterparty
                { width: 14 }, // Trade Date
                { width: 14 }, // MMBtus
                { width: 14 }, // Strike
                { width: 14 }, // Purchase Price
                { width: 16 }, // Purchase Cost
                { width: 18 }, // Trade/Expiration
                { width: 14 }, // Expiration Price
                { width: 14 }, // Realized Value
                { width: 18 }, // Net Gain/Loss
                { width: 12 }, // Fees
            ];

            // Row 1: Title - merged across all columns, bold, centered
            sheet.addRow([fyData.fiscalYearLabel]);
            sheet.mergeCells(1, 1, 1, lastCol);
            const titleCell = sheet.getCell(1, 1);
            titleCell.font = { bold: true, size: 14 };
            titleCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // Row 2: Subtitle - merged across all columns, bold, centered
            sheet.addRow([reportAsOf]);
            sheet.mergeCells(2, 1, 2, lastCol);
            const subtitleCell = sheet.getCell(2, 1);
            subtitleCell.font = { bold: true };
            subtitleCell.alignment = { horizontal: 'center', vertical: 'middle' };

            // Row 3: Headers - bold and centered
            const headerRow = sheet.addRow(HEADERS);
            headerRow.font = { bold: true };
            headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

            // Data rows
            fyData.rows
                .filter(r => r.tenureDate !== 'zzz-summary')
                .forEach(r => {
                    sheet.addRow([
                        r.formattedDate,
                        r.tool,
                        r.counterpartyName,
                        r.tradeDate,
                        r.quantity,
                        r.strike,
                        r.purchasePrice,
                        r.purchaseCost,
                        r.expirationDate,
                        r.expirationPrice,
                        r.realizedValue,
                        r.netValueGainLoss,
                        r.fees,
                    ]);
                });

            // Summary row - bold
            const summaryRow = sheet.addRow([
                'SUMMARY:',
                '',
                '',
                '',
                fyData.summary.totalQuantity,
                '',
                '',
                fyData.summary.totalPurchaseCost,
                '',
                '',
                fyData.summary.totalRealizedValue,
                fyData.summary.totalNetValueGainLoss,
                fyData.summary.totalFees,
            ]);
            summaryRow.font = { bold: true };

            // Apply number formats to data rows + summary row (data starts at row 4)
            const dataStartRow = 4;
            const lastRowNum = sheet.lastRow?.number ?? dataStartRow;
            for (let r = dataStartRow; r <= lastRowNum; r++) {
                const applyFmt = (col: number, fmt: string) => {
                    const cell = sheet.getCell(r, col);
                    if (typeof cell.value === 'number') {
                        cell.numFmt = fmt;
                    }
                };
                applyFmt(5, qtyFmt);        // MMBtus
                applyFmt(6, strikeFmt);     // Strike
                applyFmt(7, priceFmt);      // Purchase Price
                applyFmt(8, currencyFmt);   // Purchase Cost
                applyFmt(10, priceFmt);     // Expiration Price
                applyFmt(11, currencyFmt);  // Realized Value
                applyFmt(12, currencyFmt);  // Net Gain/Loss
                applyFmt(13, currencyFmt);  // Fees
            }
        });

        await downloadWorkbook(workbook);
    };

    const toggleYear = (yearLabel: string) => {
        setExpandedYears(prev => {
            const newSet = new Set(prev);
            if (newSet.has(yearLabel)) {
                newSet.delete(yearLabel);
            } else {
                newSet.add(yearLabel);
            }
            return newSet;
        });
    };

    // Group positions by fiscal year
    const fiscalYearData: FiscalYearData[] = useMemo(() => {
        if (positions.length === 0) return [];

        // Detect offsetting futures: Buy+Sell future pairs with same volume in same tenure/counterparty.
        // For a matched pair: only the long (Buy) is displayed. Its purchase cost/proceeds becomes 0
        // and its realized value becomes the net gain/loss from the offset: (sellPrice - buyPrice) * volume.
        // The short's fees roll into the long's fees so nothing is lost.
        const offsettingShortIds = new Set<number>();
        const offsetMatches = new Map<number, PositionModel>();
        const futuresByKey = new Map<string, PositionModel[]>();
        positions.forEach(p => {
            if (p.positionType !== PositionType.Future) return;
            const key = `${p.budgetDate.year}-${p.budgetDate.month}|${p.counterparty?.counterpartyId ?? ''}|${p.volume ?? 0}`;
            const arr = futuresByKey.get(key) || [];
            arr.push(p);
            futuresByKey.set(key, arr);
        });
        futuresByKey.forEach(group => {
            const buys = group.filter(p => p.side === Side.Buy);
            const sells = group.filter(p => p.side === Side.Sell);
            const pairCount = Math.min(buys.length, sells.length);
            for (let i = 0; i < pairCount; i++) {
                const buy = buys[i];
                const sell = sells[i];
                if (sell.positionId !== undefined) {
                    offsettingShortIds.add(sell.positionId);
                }
                if (buy.positionId !== undefined) {
                    offsetMatches.set(buy.positionId, sell);
                }
            }
        });

        // Create rows with fiscal year info
        const rowsWithFiscalYear = positions.map(position => {
            const customData = parseCustomData(position.customData);
            const budgetMonth = position.budgetDate.month ?? 1;
            const budgetYear = position.budgetDate.year ?? 2000;
            const fiscalYear = getFiscalYear(budgetMonth, budgetYear, fiscalMonthOffset);
            const isOffsettingShort = position.positionId !== undefined && offsettingShortIds.has(position.positionId);
            const matchedSell = position.positionId !== undefined ? offsetMatches.get(position.positionId) : undefined;

            let purchaseCost = calculatePurchaseCost(position);
            let realizedValue = calculateRealizedValue(position, quoteDate);
            // Fees are per contract, so multiply by the number of contracts (|volume| / MMBtus per contract).
            const feePerContract = customData.fees ?? customData.fee ?? 0;
            const contracts = Math.abs(position.volume ?? 0) / MMBTU_PER_CONTRACT;
            let fees = feePerContract * contracts;

            if (matchedSell) {
                const volume = position.volume ?? 0;
                const buyPrice = position.price ?? 0;
                const sellPrice = matchedSell.price ?? 0;
                purchaseCost = 0;
                realizedValue = (sellPrice - buyPrice) * volume;
                const sellCustomData = parseCustomData(matchedSell.customData);
                const sellFeePerContract = sellCustomData.fees ?? sellCustomData.fee ?? 0;
                const sellContracts = Math.abs(matchedSell.volume ?? 0) / MMBTU_PER_CONTRACT;
                fees += sellFeePerContract * sellContracts;
            }
            const netValueGainLoss = realizedValue + purchaseCost;

            return {
                fiscalYear,
                isOffsettingShort,
                row: {
                    tenureDate: `${budgetYear}-${String(budgetMonth).padStart(2, '0')}`,
                    formattedDate: dayjs(`${budgetYear}-${budgetMonth}-01`).format('MMM-YYYY'),
                    tool: getToolDescription(position),
                    counterpartyName: position.counterparty?.counterpartyName || '',
                    tradeDate: position.tradeDate ? dayjs(position.tradeDate).format('MM/DD/YYYY') : '',
                    quantity: Math.abs(position.volume ?? 0),
                    strike: position.optionStrike ?? 0,
                    purchasePrice: position.price ?? 0,
                    purchaseCost,
                    expirationDate: position.expirationDate ? dayjs(position.expirationDate).format('MM/DD/YYYY') : '',
                    // These are closed positions, so there is no expiration price to show.
                    // (This previously read markToMarketPrice, which the backend computes as
                    // Side * (MarketPrice - Price) - a per-unit gain/loss, not a price level.)
                    expirationPrice: 0,
                    realizedValue,
                    netValueGainLoss,
                    fees,
                    position,
                } as MtmClosedReportRow,
            };
        });

        // Group by fiscal year - exclude offsetting shorts from display (their values were rolled into the long)
        const grouped = new Map<number, { rows: MtmClosedReportRow[] }>();
        rowsWithFiscalYear.forEach(item => {
            if (item.isOffsettingShort) return;
            const existing = grouped.get(item.fiscalYear) || { rows: [] };
            existing.rows.push(item.row);
            grouped.set(item.fiscalYear, existing);
        });

        // Convert to array and sort by fiscal year
        const result: FiscalYearData[] = [];
        const sortedYears = Array.from(grouped.keys()).sort((a, b) => a - b);

        sortedYears.forEach(fiscalYear => {
            const data = grouped.get(fiscalYear)!;

            // Sort rows within fiscal year
            data.rows.sort((a, b) => {
                const dateCompare = a.tenureDate.localeCompare(b.tenureDate);
                if (dateCompare !== 0) return dateCompare;
                return a.tradeDate.localeCompare(b.tradeDate);
            });

            // Calculate fiscal year date range
            const startMonthName = MONTH_NAMES[fiscalMonthOffset];
            const endMonthIndex = fiscalMonthOffset === 1 ? 11 : fiscalMonthOffset - 1;
            const endMonthName = MONTH_NAMES[endMonthIndex];
            const fyStartYear = fiscalYear - 1;
            const fyEndYear = fiscalYear;

            // Add summary row - aggregate from displayed rows so offsetting adjustments are reflected.
            // Quantity still only counts Buy-side positions per MMBtus rule.
            const summary: ClosedReportSummary = data.rows.reduce<ClosedReportSummary>((acc, r) => ({
                totalQuantity: acc.totalQuantity + (r.position.side === Side.Buy ? (r.position.volume ?? 0) : 0),
                totalPurchaseCost: acc.totalPurchaseCost + r.purchaseCost,
                totalRealizedValue: acc.totalRealizedValue + r.realizedValue,
                totalNetValueGainLoss: acc.totalNetValueGainLoss + r.netValueGainLoss,
                totalFees: acc.totalFees + r.fees,
            }), {
                totalQuantity: 0,
                totalPurchaseCost: 0,
                totalRealizedValue: 0,
                totalNetValueGainLoss: 0,
                totalFees: 0,
            });
            const rowsWithSummary: MtmClosedReportRow[] = [
                ...data.rows,
                {
                    tenureDate: 'zzz-summary',
                    formattedDate: 'SUMMARY:',
                    tool: '',
                    counterpartyName: '',
                    tradeDate: '',
                    quantity: summary.totalQuantity,
                    strike: 0,
                    purchasePrice: 0,
                    purchaseCost: summary.totalPurchaseCost,
                    expirationDate: '',
                    expirationPrice: 0,
                    realizedValue: summary.totalRealizedValue,
                    netValueGainLoss: summary.totalNetValueGainLoss,
                    fees: summary.totalFees,
                    position: {} as PositionModel,
                }
            ];

            result.push({
                fiscalYearLabel: `${selectedLocationName} ${startMonthName}-${fyStartYear}/${endMonthName}-${fyEndYear} Review`,
                startDate: `${startMonthName} ${fyStartYear}`,
                endDate: `${endMonthName} ${fyEndYear}`,
                rows: rowsWithSummary,
                summary,
            });
        });

        return result;
    }, [positions, fiscalMonthOffset, selectedLocationName, quoteDate]);

    // One row per fiscal year plus a grand total, for the consolidated summary.
    const consolidatedSummaryRows: ConsolidatedSummaryRow[] = useMemo(() => {
        if (fiscalYearData.length === 0) return [];

        const rows: ConsolidatedSummaryRow[] = fiscalYearData.map(fyData => ({
            fiscalYearLabel: fyData.fiscalYearLabel,
            totalQuantity: fyData.summary.totalQuantity,
            totalPurchaseCost: fyData.summary.totalPurchaseCost,
            totalRealizedValue: fyData.summary.totalRealizedValue,
            totalNetValueGainLoss: fyData.summary.totalNetValueGainLoss,
            totalFees: fyData.summary.totalFees,
        }));

        const total = rows.reduce<ConsolidatedSummaryRow>((acc, row) => ({
            fiscalYearLabel: 'TOTAL',
            totalQuantity: acc.totalQuantity + row.totalQuantity,
            totalPurchaseCost: acc.totalPurchaseCost + row.totalPurchaseCost,
            totalRealizedValue: acc.totalRealizedValue + row.totalRealizedValue,
            totalNetValueGainLoss: acc.totalNetValueGainLoss + row.totalNetValueGainLoss,
            totalFees: acc.totalFees + row.totalFees,
        }), {
            fiscalYearLabel: 'TOTAL',
            totalQuantity: 0,
            totalPurchaseCost: 0,
            totalRealizedValue: 0,
            totalNetValueGainLoss: 0,
            totalFees: 0,
        });

        return [...rows, total];
    }, [fiscalYearData]);

    const columnDefs: ColDef<MtmClosedReportRow>[] = useMemo(() => {
        const cols: ColDef<MtmClosedReportRow>[] = [
            {
                field: 'formattedDate',
                headerName: 'Period',
                width: 100,
                pinned: 'left',
                sort: 'asc',
                comparator: (_a, _b, nodeA, nodeB) => {
                    const ta = nodeA.data?.tenureDate ?? '';
                    const tb = nodeB.data?.tenureDate ?? '';
                    return ta.localeCompare(tb);
                },
            },
            { field: 'tool', headerName: 'Tool', width: 100 },
            { field: 'counterpartyName', headerName: 'Counterparty', width: 150 },
            { field: 'tradeDate', headerName: 'Original Trade Date', width: 130 },
            { 
                field: 'quantity', 
                headerName: 'MMBtus Purchased Per Month', 
                width: 180,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || ''
            },
            { 
                field: 'strike', 
                headerName: 'Strike/Fixed Price', 
                width: 130,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(3)}` : ''
            },
            { 
                field: 'purchasePrice', 
                headerName: 'Purchase Price', 
                width: 120,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(4)}` : ''
            },
            { 
                field: 'purchaseCost', 
                headerName: 'Purchase Cost/Proceeds', 
                width: 160,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
            { 
                field: 'expirationDate', 
                headerName: 'Trade / Expiration Date', 
                width: 160
            },
            { 
                field: 'expirationPrice', 
                headerName: 'Expiration Price', 
                width: 130,
                type: 'numericColumn',
                valueFormatter: params => params.value ? `$${params.value.toFixed(4)}` : ''
            },
            { 
                field: 'realizedValue', 
                headerName: 'Realized Value', 
                width: 130,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
            { 
                field: 'netValueGainLoss', 
                headerName: 'Net Value Realized Gain or (Loss)', 
                width: 200,
                type: 'numericColumn',
                cellStyle: params => ({
                    color: (params.value ?? 0) >= 0 ? 'green' : 'black'
                }),
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            },
        ];

        if (includeFees) {
            cols.push({
                field: 'fees',
                headerName: 'Fees',
                width: 100,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || ''
            });
        }

        return cols;
    }, [includeFees]);

    const consolidatedColumnDefs: ColDef<ConsolidatedSummaryRow>[] = useMemo(() => {
        const currency = (params: { value?: number | null }) =>
            params.value?.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) || '';

        const cols: ColDef<ConsolidatedSummaryRow>[] = [
            { field: 'fiscalYearLabel', headerName: 'Fiscal Year', width: 220, pinned: 'left' },
            {
                field: 'totalQuantity',
                headerName: 'MMBtus',
                width: 140,
                type: 'numericColumn',
                valueFormatter: params => params.value?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || ''
            },
            { field: 'totalPurchaseCost', headerName: 'Purchase Cost/Proceeds', width: 190, type: 'numericColumn', valueFormatter: currency },
            { field: 'totalRealizedValue', headerName: 'Realized Value', width: 150, type: 'numericColumn', valueFormatter: currency },
            {
                field: 'totalNetValueGainLoss',
                headerName: 'Net Value Realized Gain or (Loss)',
                width: 220,
                type: 'numericColumn',
                cellStyle: params => ({ color: (params.value ?? 0) >= 0 ? 'green' : 'black' }),
                valueFormatter: currency
            },
        ];

        if (includeFees) {
            cols.push({ field: 'totalFees', headerName: 'Fees', width: 120, type: 'numericColumn', valueFormatter: currency });
        }

        return cols;
    }, [includeFees]);

    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>MTM Closed Positions Report</Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <StoneXRow>
                    <StoneXDatePicker
                        label="MTM Date"
                        value={quoteDate}
                        onChange={(date) => setQuoteDate(date || new Date())}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Start Month</InputLabel>
                        <Select
                            value={startMonth}
                            label="Start Month"
                            onChange={(e) => setStartMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Start Year"
                        type="number"
                        size="small"
                        value={startYear}
                        onChange={(e) => setStartYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>End Month</InputLabel>
                        <Select
                            value={endMonth}
                            label="End Month"
                            onChange={(e) => setEndMonth(e.target.value as number)}
                        >
                            {MONTHS.map(m => (
                                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="End Year"
                        type="number"
                        size="small"
                        value={endYear}
                        onChange={(e) => setEndYear(parseInt(e.target.value) || dayjs().year())}
                        sx={{ width: 100 }}
                    />
                </StoneXRow>
                <StoneXRow>
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Location</InputLabel>
                        <Select
                            value={selectedLocation ?? ''}
                            label="Location"
                            onChange={(e) => setSelectedLocation(e.target.value as number)}
                        >
                            {locations.map((l: LocationModel) => (
                                <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <FormControlLabel
                        control={<Checkbox checked={includeFees} onChange={(e) => setIncludeFees(e.target.checked)} />}
                        label="Include Fees"
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Report View</InputLabel>
                        <Select
                            value={reportView}
                            label="Report View"
                            onChange={(e) => setReportView(e.target.value as ReportView)}
                        >
                            {REPORT_VIEWS.map(v => (
                                <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </StoneXRow>
                <StoneXRow>
                    <Button variant="contained" onClick={handleSubmit}>Submit</Button>
                    <Button
                        variant="outlined"
                        onClick={handleDownloadExcel}
                        disabled={!hasRun || fiscalYearData.length === 0}
                        sx={{ ml: 1 }}
                    >
                        Download Excel
                    </Button>
                </StoneXRow>
            </Paper>

            {loadingState.isLoading() ? (
                <StoneXLoading show />
            ) : hasRun ? (
                <>
                    {fiscalYearData.map((fyData, index) => (
                        <Paper key={fyData.fiscalYearLabel} sx={{ mb: 3 }}>
                            <Box 
                                sx={{ 
                                    p: 2, 
                                    backgroundColor: 'primary.main', 
                                    color: 'primary.contrastText',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    cursor: 'pointer'
                                }}
                                onClick={() => toggleYear(fyData.fiscalYearLabel)}
                            >
                                <Box>
                                    <Typography variant="h6">{fyData.fiscalYearLabel}</Typography>
                                    <Typography variant="body2">Report as of {dayjs(quoteDate).format('MM/DD/YYYY')} Close</Typography>
                                </Box>
                                <IconButton sx={{ color: 'inherit' }}>
                                    {expandedYears.has(fyData.fiscalYearLabel) ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                            </Box>

                            <Collapse in={expandedYears.has(fyData.fiscalYearLabel)}>
                                {showDetail && (
                                    <StoneXGrid
                                        columns={columnDefs}
                                        rows={fyData.rows.filter(r => r.tenureDate !== 'zzz-summary')}
                                        hideGridOptions={true}
                                    />
                                )}
                            </Collapse>

                            {/* Summary row always visible */}
                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderTop: '1px solid #ddd' }}>
                                <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <Typography><strong>SUMMARY:</strong></Typography>
                                    <Typography>
                                        <strong>MMBtus:</strong> {fyData.summary.totalQuantity.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                    </Typography>
                                    <Typography>
                                        <strong>Purchase Cost:</strong> {fyData.summary.totalPurchaseCost.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography>
                                        <strong>Realized Value:</strong> {fyData.summary.totalRealizedValue.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })}
                                    </Typography>
                                    <Typography>
                                        <strong>Net Gain/(Loss):</strong>{' '}
                                        <span style={{ color: fyData.summary.totalNetValueGainLoss >= 0 ? 'green' : 'black' }}>
                                            {fyData.summary.totalNetValueGainLoss.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })}
                                        </span>
                                    </Typography>
                                    {includeFees && (
                                        <Typography>
                                            <strong>Fees:</strong> {fyData.summary.totalFees.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 })}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        </Paper>
                    ))}

                    {showSummary && consolidatedSummaryRows.length > 0 && (
                        <Paper sx={{ mb: 3 }}>
                            <Box sx={{ p: 2, backgroundColor: 'primary.main', color: 'primary.contrastText' }}>
                                <Typography variant="h6">Summary - All Fiscal Years</Typography>
                                <Typography variant="body2">Report as of {dayjs(quoteDate).format('MM/DD/YYYY')} Close</Typography>
                            </Box>
                            <StoneXGrid
                                columns={consolidatedColumnDefs}
                                rows={consolidatedSummaryRows}
                                hideGridOptions={true}
                            />
                        </Paper>
                    )}
                </>
            ) : null}
        </Box>
    );
};

export default MtmClosedReport;
